// src/services/loggingService.js
// Logging service for sending logs to backend /mobile-logs endpoint
import { fetchAuthSession } from 'aws-amplify/auth';

class LoggingService {
  constructor() {
    this.logQueue = [];
    this.maxQueueSize = 1000;
    this.batchSize = 50;
    this.maxBatchSizeBytes = 128 * 1024; // 128KB
    this.isFlushing = false;
    this.flushTimer = null;
    this.sessionId = this.generateSessionId();
    this.traceId = this.generateTraceId();
    this.interactionId = null;
    this.currentScreen = null;
    this.currentRoute = null;
    this.currentVertical = null;
    this.userId = null;
    this.tenantId = null;
    this.appVersion = process.env.REACT_APP_VERSION || '1.0.0';
    this.apiBaseUrl =
      process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';
  }

  // Initialize the service
  async initialize(userId, tenantId, vertical) {
    this.userId = userId;
    this.tenantId = tenantId;
    this.currentVertical = vertical;
    
    // Warn if tenantId is missing (will cause partition issues in Athena)
    if (!this.tenantId) {
      console.warn(
        '[LoggingService] Warning: tenantId is null or undefined. ' +
        'This may cause logs to be partitioned incorrectly in S3/Athena. ' +
        'Ensure tenantId is extracted from Cognito token.'
      );
    }
    
    this.loadLogsFromStorage(); // Load offline logs
    this.startPeriodicFlush();

    // Set up online/offline listeners
    window.addEventListener('online', () => {
      this.flushLogs();
    });

    window.addEventListener('beforeunload', () => {
      this.forceFlush();
    });
  }

  // Generate unique IDs
  generateSessionId() {
    return `s-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  }

  generateTraceId() {
    return `tr-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  }

  generateInteractionId() {
    return `interaction-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  }

  // Set current screen/route
  setCurrentScreen(screen, route = null) {
    this.currentScreen = screen;
    this.currentRoute = route || screen;
    this.interactionId = this.generateInteractionId();
  }

  // Set current vertical
  setCurrentVertical(vertical) {
    this.currentVertical = vertical;
  }

  // Update user context
  updateUserContext(userId, tenantId) {
    this.userId = userId;
    this.tenantId = tenantId;
  }

  // Main logging methods
  async logInfo(eventType, message, extra = null) {
    await this.addLog('INFO', eventType, message, null, extra);
  }

  async logWarning(eventType, message, extra = null) {
    await this.addLog('WARN', eventType, message, null, extra);
  }

  async logError(eventType, message, error = null, extra = null) {
    const errorObj = error
      ? {
          type: error.constructor?.name || 'Error',
          message: this.redactSensitiveData(error.message || String(error)),
          stack: error.stack || null,
        }
      : null;
    await this.addLog('ERROR', eventType, message, errorObj, extra);
  }

  async logDebug(eventType, message, extra = null) {
    // Skip DEBUG logs in production
    if (process.env.NODE_ENV === 'production') {
      return;
    }
    await this.addLog('DEBUG', eventType, message, null, extra);
  }

  // Add log to queue
  async addLog(severity, eventType, message, error, extra) {
    // Check queue overflow
    if (this.logQueue.length >= this.maxQueueSize) {
      console.warn('[LoggingService] Queue overflow, dropping log');
      return;
    }

    // Build log event
    const logEvent = await this.buildLogEvent(
      severity,
      eventType,
      message,
      error,
      extra
    );

    // Validate log format before adding to queue
    const validationError = this.validateLogFormat(logEvent);
    if (validationError) {
      console.warn('[LoggingService] Invalid log format, attempting migration:', validationError);
      const migratedLog = this.migrateLogFormat(logEvent);
      if (migratedLog) {
        const migratedValidation = this.validateLogFormat(migratedLog);
        if (migratedValidation) {
          // Allow log to be queued if only userId/tenantId/sessionId are missing (filled at send time via token)
          if (this.isOnlyContextMissing(migratedValidation)) {
            this.logQueue.push(migratedLog);
          } else {
            console.error('[LoggingService] Log validation failed after migration, dropping log:', migratedValidation);
            return;
          }
        } else {
          // Use migrated log
          this.logQueue.push(migratedLog);
        }
      } else {
        console.error('[LoggingService] Log migration failed, dropping log');
        return;
      }
    } else {
      // Add to queue
      this.logQueue.push(logEvent);
    }

    // Save to localStorage for offline persistence
    this.saveLogsToStorage();

    // Check if should flush
    if (await this.shouldFlush()) {
      await this.flushLogs();
    }
  }

  /**
   * Normalize timestamp to UTC format (always ends with 'Z')
   * Handles timestamps in various formats and converts them to UTC ISO string
   * 
   * @param {string|Date} timestamp - Timestamp in any format
   * @returns {string} UTC timestamp in ISO format (e.g., '2026-02-23T14:32:51.690Z')
   */
  normalizeTimestampToUTC(timestamp) {
    if (!timestamp) {
      // If no timestamp provided, use current time in UTC
      return new Date().toISOString();
    }

    try {
      // If it's already a Date object, convert to UTC ISO string
      if (timestamp instanceof Date) {
        return timestamp.toISOString();
      }

      // If it's a string, parse it
      if (typeof timestamp === 'string') {
        // Check if it already ends with 'Z' (UTC indicator)
        if (timestamp.endsWith('Z')) {
          // Validate it's a proper UTC timestamp
          const date = new Date(timestamp);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        }

        // Parse the timestamp (handles timezone offsets like +05:30, -05:00, etc.)
        const date = new Date(timestamp);
        
        if (isNaN(date.getTime())) {
          console.warn('[LoggingService] Invalid timestamp format, using current UTC time:', timestamp);
          return new Date().toISOString();
        }

        // Convert to UTC ISO string (always ends with 'Z')
        return date.toISOString();
      }

      // Fallback: use current UTC time
      console.warn('[LoggingService] Unexpected timestamp type, using current UTC time:', typeof timestamp);
      return new Date().toISOString();
    } catch (error) {
      console.error('[LoggingService] Error normalizing timestamp to UTC:', error);
      // Fallback: use current UTC time
      return new Date().toISOString();
    }
  }

  /**
   * Build log event object
   * 
   * Format requirements:
   * - eventType: camelCase (not event_type or type)
   * - message: "message" (not msg)
   * - severity: Always uppercase "INFO", "DEBUG", "WARN", "ERROR"
   * - timestamp: Always UTC format ending with 'Z'
   * 
   * @param {string} severity - Log severity (will be normalized to uppercase)
   * @param {string} eventType - Event type in camelCase
   * @param {string} message - Log message
   * @param {Object|null} error - Error object (if any)
   * @param {Object|null} extra - Extra metadata
   * @returns {Object} Log event object
   */
  async buildLogEvent(severity, eventType, message, error, extra) {
    const deviceInfo = this.getDeviceInfo();
    const networkType = await this.getNetworkType();

    // Ensure severity is always uppercase (INFO, DEBUG, WARN, ERROR)
    const normalizedSeverity = typeof severity === 'string' ? severity.toUpperCase() : 'INFO';

    return {
      timestamp: this.normalizeTimestampToUTC(new Date()),
      eventType, // camelCase - not event_type or type
      severity: normalizedSeverity, // Always uppercase: INFO, DEBUG, WARN, ERROR
      message: this.redactSensitiveData(message), // message - not msg
      userId: this.userId ?? null,
      tenantId: this.tenantId ?? null,
      vertical: this.currentVertical ?? null,
      sessionId: this.sessionId ?? null,
      traceId: this.traceId ?? null,
      screen: this.currentScreen ?? (typeof window !== 'undefined' && window.location ? (window.location.pathname.split('/').pop() || 'home') : 'unknown'),
      appVersion: this.appVersion,
      platform: 'web',
      device: deviceInfo,
      locale: navigator.language || 'en-US',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      networkType: networkType || 'unknown',
      batterySaver: false, // Always false for web
      interactionId: this.interactionId ?? null,
      ...(error && { error }),
      ...(extra && { extra: this.redactMap(extra) }),
    };
  }

  // Get device/browser information
  getDeviceInfo() {
    const ua = navigator.userAgent;
    const browser = this.getBrowser(ua);
    return {
      browser: browser.name,
      version: browser.version,
      userAgent: ua,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };
  }

  // Get browser name and version
  getBrowser(userAgent) {
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      const match = userAgent.match(/Chrome\/(\d+)/);
      return { name: 'Chrome', version: match ? match[1] : 'unknown' };
    } else if (userAgent.includes('Firefox')) {
      const match = userAgent.match(/Firefox\/(\d+)/);
      return { name: 'Firefox', version: match ? match[1] : 'unknown' };
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      const match = userAgent.match(/Version\/(\d+)/);
      return { name: 'Safari', version: match ? match[1] : 'unknown' };
    } else if (userAgent.includes('Edg')) {
      const match = userAgent.match(/Edg\/(\d+)/);
      return { name: 'Edge', version: match ? match[1] : 'unknown' };
    }
    return { name: 'Unknown', version: 'unknown' };
  }

  // Get network type
  async getNetworkType() {
    if ('connection' in navigator) {
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
      if (connection) {
        const effectiveType = connection.effectiveType || connection.type;
        if (effectiveType) {
          // Map effectiveType to network types
          if (effectiveType.includes('4g')) return 'cellular';
          if (effectiveType.includes('3g')) return 'cellular';
          if (effectiveType.includes('2g')) return 'cellular';
          if (effectiveType === 'wifi') return 'wifi';
          if (effectiveType === 'ethernet') return 'ethernet';
          return effectiveType;
        }
      }
    }
    // Fallback: check if we're online
    return navigator.onLine ? 'unknown' : 'offline';
  }

  // Check if should flush logs
  async shouldFlush() {
    // Check if online
    if (!navigator.onLine) {
      return false;
    }

    // Check if queue is full
    if (this.logQueue.length >= this.batchSize) {
      return true;
    }

    // Check network quality (for web, we can check connection type)
    const networkType = await this.getNetworkType();
    if (networkType === 'slow-2g' || networkType === '2g') {
      // Only flush if queue is getting full
      return this.logQueue.length >= this.batchSize / 2;
    }

    // Default: flush if we have logs and are online
    return this.logQueue.length > 0;
  }

  // Flush logs to server
  async flushLogs() {
    if (this.logQueue.length === 0 || this.isFlushing) {
      return;
    }

    this.isFlushing = true;

    try {
      // Create batches
      const batches = this.createBatches();

      for (const batch of batches) {
        if (batch.length === 0) continue;

        try {
          await this.sendBatch(batch);
          // Remove sent logs from queue
          this.logQueue.splice(0, batch.length);
        } catch (error) {
          console.error('[LoggingService] Error sending batch:', error);
          // Keep logs in queue for retry
          break;
        }
      }

      // Save remaining logs to storage
      this.saveLogsToStorage();
    } catch (error) {
      console.error('[LoggingService] Error in flush operation:', error);
    } finally {
      this.isFlushing = false;
    }
  }

  // Create batches from queue
  createBatches() {
    const batches = [];
    let currentBatch = [];
    let currentBatchSize = 0;

    for (const log of this.logQueue) {
      const logSize = JSON.stringify(log).length;

      // Check if log is too large
      if (logSize > this.maxBatchSizeBytes) {
        console.warn('[LoggingService] Log too large, skipping');
        continue;
      }

      // Check if adding this log would exceed limits
      if (
        currentBatchSize + logSize > this.maxBatchSizeBytes ||
        currentBatch.length >= this.batchSize
      ) {
        if (currentBatch.length > 0) {
          batches.push([...currentBatch]);
          currentBatch = [];
          currentBatchSize = 0;
        }
      }

      currentBatch.push(log);
      currentBatchSize += logSize;
    }

    if (currentBatch.length > 0) {
      batches.push(currentBatch);
    }

    return batches;
  }

  // Validate log format according to API requirements
  validateLogFormat(log) {
    const errors = [];

    // Check required fields
    if (!log.eventType) {
      errors.push('eventType is required (not event_type or type)');
    }
    if (!log.message) {
      errors.push('message is required (not msg)');
    }
    if (!log.severity) {
      errors.push('severity is required');
    } else {
      const validSeverities = ['DEBUG', 'INFO', 'WARN', 'ERROR'];
      if (!validSeverities.includes(log.severity)) {
        errors.push(`severity must be one of: ${validSeverities.join(', ')} (got: ${log.severity})`);
      }
    }
    if (!log.timestamp) {
      errors.push('timestamp is required');
    } else {
      // Validate timestamp is in UTC format (ends with 'Z')
      if (typeof log.timestamp === 'string' && !log.timestamp.endsWith('Z')) {
        errors.push('timestamp must be in UTC format (ending with Z)');
      }
    }
    if (!log.userId) {
      errors.push('userId is required');
    }
    if (!log.tenantId) {
      errors.push('tenantId is required');
    }
    if (!log.sessionId) {
      errors.push('sessionId is required');
    }

    // Check for invalid field names
    if (log.event_type) {
      errors.push('event_type is not allowed, use eventType');
    }
    if (log.type) {
      errors.push('type is not allowed, use eventType');
    }
    if (log.msg) {
      errors.push('msg is not allowed, use message');
    }

    return errors.length > 0 ? errors.join('; ') : null;
  }

  /**
   * Returns true if the validation error is only about userId, tenantId, or sessionId.
   * Those can be filled at send time via enrichLogWithTokenData, so we allow the log to be queued.
   */
  isOnlyContextMissing(validationError) {
    if (!validationError || typeof validationError !== 'string') return false;
    const onlyContext = /^(userId is required|tenantId is required|sessionId is required)(; (userId is required|tenantId is required|sessionId is required))*;?$/;
    const parts = validationError.split('; ').map(s => s.trim()).filter(Boolean);
    const contextReqs = ['userId is required', 'tenantId is required', 'sessionId is required'];
    return parts.length > 0 && parts.every(p => contextReqs.includes(p));
  }

  // Migrate old log format to new format
  migrateLogFormat(log) {
    const migrated = { ...log };

    // Migrate event_type or type to eventType
    if (migrated.event_type && !migrated.eventType) {
      migrated.eventType = migrated.event_type;
      delete migrated.event_type;
    }
    if (migrated.type && !migrated.eventType) {
      migrated.eventType = migrated.type;
      delete migrated.type;
    }

    // Migrate msg to message
    if (migrated.msg && !migrated.message) {
      migrated.message = migrated.msg;
      delete migrated.msg;
    }

    // Normalize severity to uppercase
    if (migrated.severity && typeof migrated.severity === 'string') {
      migrated.severity = migrated.severity.toUpperCase();
    }

    // Normalize timestamp to UTC (always ends with 'Z')
    if (migrated.timestamp) {
      migrated.timestamp = this.normalizeTimestampToUTC(migrated.timestamp);
    }

    // Fill userId/tenantId/sessionId from current service state when missing (e.g. from stored or pre-init logs)
    if (!migrated.userId && this.userId != null) {
      migrated.userId = this.userId;
    }
    if (!migrated.tenantId && this.tenantId != null) {
      migrated.tenantId = this.tenantId;
    }
    if (!migrated.sessionId && this.sessionId != null) {
      migrated.sessionId = this.sessionId;
    }

    return migrated;
  }

  // Try to extract userId and tenantId from token if missing
  // Also normalizes timestamp to UTC
  async enrichLogWithTokenData(log) {
    if (!log.userId || !log.tenantId) {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        
        if (idToken && idToken.payload) {
          const payload = idToken.payload;
          
          // Extract userId if missing
          if (!log.userId) {
            log.userId = payload.sub || payload['cognito:username'] || null;
          }
          
          // Extract tenantId if missing
          if (!log.tenantId) {
            log.tenantId = payload['custom:tenant_id'] || payload.tenant_id || null;
          }
        }
      } catch (error) {
        console.warn('[LoggingService] Failed to enrich log with token data:', error);
      }
    }
    
    // Always normalize timestamp to UTC before sending
    if (log.timestamp) {
      log.timestamp = this.normalizeTimestampToUTC(log.timestamp);
    } else {
      // If timestamp is missing, add current UTC time
      log.timestamp = new Date().toISOString();
    }
    
    return log;
  }

  // Send batch to server
  async sendBatch(batch) {
    try {
      // Get authentication token
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('Authentication token not found');
      }

      // Enrich logs with token data if userId or tenantId are missing
      // This also normalizes all timestamps to UTC
      let enrichedBatch = await Promise.all(
        batch.map(log => this.enrichLogWithTokenData({ ...log }))
      );

      // Double-check: Ensure all timestamps are in UTC format (defensive programming)
      enrichedBatch = enrichedBatch.map(log => ({
        ...log,
        timestamp: this.normalizeTimestampToUTC(log.timestamp)
      }));

      // Validate all logs in batch before sending
      const validationErrors = [];
      for (let i = 0; i < enrichedBatch.length; i++) {
        const error = this.validateLogFormat(enrichedBatch[i]);
        if (error) {
          validationErrors.push(`Log ${i}: ${error}`);
        }
      }

      if (validationErrors.length > 0) {
        console.error('[LoggingService] Batch validation failed:', validationErrors);
        // Filter out invalid logs
        const validLogs = enrichedBatch.filter(log => !this.validateLogFormat(log));
        if (validLogs.length === 0) {
          throw new Error('All logs in batch failed validation');
        }
        console.warn(`[LoggingService] Sending ${validLogs.length} of ${enrichedBatch.length} valid logs`);
        enrichedBatch = validLogs;
      }

      const url = `${this.apiBaseUrl}/mobile-logs`;

      // Build headers
      // Note: Backend CORS policy only allows standard headers (Authorization, Content-Type)
      // All metadata (traceId, sessionId, userId, etc.) is included in the log event body
      // This matches the pattern used by other API services in the codebase
      const headers = {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      };

      const requestBody = JSON.stringify(enrichedBatch);

      // Log request details in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('[LoggingService] Sending batch:', {
          url,
          logCount: enrichedBatch.length,
          batchSize: requestBody.length,
          sampleLog: enrichedBatch[0],
        });
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: requestBody,
      });

      if (!response.ok) {
        let errorData;
        let errorText;
        try {
          errorText = await response.text();
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText || 'Unknown error' };
        }

        // Enhanced error logging
        console.error('[LoggingService] Backend error response:', {
          status: response.status,
          statusText: response.statusText,
          errorData,
          requestBody: process.env.NODE_ENV === 'development' ? enrichedBatch : '[hidden]',
        });

        throw new Error(
          `Failed to send logs: ${response.status} - ${errorData.message || errorData.error || errorData.detail || 'Unknown error'}`
        );
      }

      const responseData = await response.json();
      console.log(
        `[LoggingService] Successfully sent ${enrichedBatch.length} logs to backend`,
        responseData
      );

      // Log S3 key for debugging Athena partition issues
      if (responseData.s3_key) {
        console.log(
          `[LoggingService] Logs stored in S3: ${responseData.s3_key}. ` +
          `If logs don't appear in Athena, run: MSCK REPAIR TABLE mobile_logs;`
        );
      }

      return responseData;
    } catch (error) {
      console.error('[LoggingService] Error sending logs:', error);
      throw error;
    }
  }

  // Get correlation headers (for use by other services)
  getCorrelationHeaders() {
    return {
      'X-Trace-Id': this.traceId || '',
      'X-Session-Id': this.sessionId || '',
      'X-Vertical': this.currentVertical || '',
      'X-App-Version': this.appVersion || '',
      'X-Interaction-Id': this.interactionId || '',
      'X-Screen': this.currentScreen || '',
    };
  }

  // Start periodic flush
  startPeriodicFlush() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(async () => {
      if (await this.shouldFlush()) {
        await this.flushLogs();
      }
    }, 30000); // Every 30 seconds
  }

  // Stop periodic flush
  stopPeriodicFlush() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  // Save logs to localStorage for offline persistence
  saveLogsToStorage() {
    try {
      const logsToSave = this.logQueue.slice(0, this.maxQueueSize);
      localStorage.setItem('logQueue', JSON.stringify(logsToSave));
    } catch (error) {
      // Handle quota exceeded error
      if (error.name === 'QuotaExceededError') {
        console.warn(
          '[LoggingService] localStorage quota exceeded, clearing old logs'
        );
        // Try to save only the most recent logs
        try {
          const recentLogs = this.logQueue.slice(-100);
          localStorage.setItem('logQueue', JSON.stringify(recentLogs));
        } catch (e) {
          console.error('[LoggingService] Error saving logs to storage:', e);
        }
      } else {
        console.error('[LoggingService] Error saving logs to storage:', error);
      }
    }
  }

  // Load logs from localStorage
  loadLogsFromStorage() {
    try {
      const stored = localStorage.getItem('logQueue');
      if (stored) {
        const parsedLogs = JSON.parse(stored);
        if (Array.isArray(parsedLogs)) {
          // Normalize all timestamps to UTC when loading from storage
          this.logQueue = parsedLogs.map(log => {
            if (log.timestamp) {
              return {
                ...log,
                timestamp: this.normalizeTimestampToUTC(log.timestamp)
              };
            }
            return log;
          });
          console.log(
            `[LoggingService] Loaded ${parsedLogs.length} logs from storage (timestamps normalized to UTC)`
          );
        }
      }
    } catch (error) {
      console.error('[LoggingService] Error loading logs from storage:', error);
      // Clear corrupted data
      try {
        localStorage.removeItem('logQueue');
      } catch (e) {
        // Ignore
      }
    }
  }

  // Redact sensitive data
  redactSensitiveData(input) {
    if (typeof input !== 'string') {
      return input;
    }

    // Redact email addresses
    input = input.replace(
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
      '[EMAIL_REDACTED]'
    );

    // Redact phone numbers
    input = input.replace(
      /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g,
      '[PHONE_REDACTED]'
    );

    // Redact credit card numbers
    input = input.replace(
      /\b\d{4}[- ]?\d{4}[- ]?\d{4}[- ]?\d{4}\b/g,
      '[CARD_REDACTED]'
    );

    // Redact tokens (long alphanumeric strings)
    input = input.replace(/\b[A-Za-z0-9]{32,}\b/g, (match) => {
      if (match.length > 32) return '[TOKEN_REDACTED]';
      return match;
    });

    return input;
  }

  // Redact sensitive data from map
  redactMap(data) {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const redacted = {};
    for (const [key, value] of Object.entries(data)) {
      // Skip sensitive keys
      if (
        key.toLowerCase().includes('password') ||
        key.toLowerCase().includes('token') ||
        key.toLowerCase().includes('secret') ||
        key.toLowerCase().includes('key') ||
        key.toLowerCase().includes('auth')
      ) {
        redacted[key] = '[REDACTED]';
      } else if (typeof value === 'string') {
        redacted[key] = this.redactSensitiveData(value);
      } else if (typeof value === 'object' && value !== null) {
        redacted[key] = this.redactMap(value);
      } else {
        redacted[key] = value;
      }
    }
    return redacted;
  }

  // Force flush (for app close, critical errors, etc.)
  async forceFlush() {
    this.stopPeriodicFlush();
    if (navigator.onLine && this.logQueue.length > 0) {
      await this.flushLogs();
    }
  }

  // Clear logs (for testing or logout)
  clearLogs() {
    this.logQueue = [];
    try {
      localStorage.removeItem('logQueue');
    } catch (error) {
      // Ignore
    }
  }

  // ========== DEBUGGING & INSPECTION METHODS ==========

  /**
   * Get current queue status
   * @returns {Object} Queue status information
   */
  getQueueStatus() {
    return {
      queueLength: this.logQueue.length,
      maxQueueSize: this.maxQueueSize,
      batchSize: this.batchSize,
      isFlushing: this.isFlushing,
      sessionId: this.sessionId,
      traceId: this.traceId,
      currentScreen: this.currentScreen,
      currentRoute: this.currentRoute,
      userId: this.userId,
      tenantId: this.tenantId,
      vertical: this.currentVertical,
      appVersion: this.appVersion,
      apiBaseUrl: this.apiBaseUrl,
    };
  }

  /**
   * Get all logs currently in queue
   * @param {number} limit - Maximum number of logs to return (default: all)
   * @returns {Array} Array of log events
   */
  getQueuedLogs(limit = null) {
    if (limit) {
      return this.logQueue.slice(0, limit);
    }
    return [...this.logQueue];
  }

  /**
   * Get logs from localStorage
   * @returns {Array} Array of log events from storage
   */
  getStoredLogs() {
    try {
      const stored = localStorage.getItem('logQueue');
      if (stored) {
        return JSON.parse(stored);
      }
      return [];
    } catch (error) {
      console.error('[LoggingService] Error reading stored logs:', error);
      return [];
    }
  }

  /**
   * Print queue status to console
   */
  printStatus() {
    const status = this.getQueueStatus();
    console.group('📊 Logging Service Status');
    console.log('Queue:', `${status.queueLength}/${status.maxQueueSize} logs`);
    console.log('Flushing:', status.isFlushing ? 'Yes' : 'No');
    console.log('Session ID:', status.sessionId);
    console.log('Trace ID:', status.traceId);
    console.log('Current Screen:', status.currentScreen);
    console.log('Current Route:', status.currentRoute);
    console.log('User ID:', status.userId);
    console.log('Tenant ID:', status.tenantId);
    console.log('Vertical:', status.vertical);
    console.log('App Version:', status.appVersion);
    console.log('API Base URL:', status.apiBaseUrl);
    console.groupEnd();
  }

  /**
   * Print recent logs to console
   * @param {number} count - Number of recent logs to show (default: 10)
   */
  printRecentLogs(count = 10) {
    const logs = this.getQueuedLogs(count);
    console.group(`📝 Recent Logs (${logs.length} of ${this.logQueue.length} total)`);
    if (logs.length === 0) {
      console.log('No logs in queue');
    } else {
      logs.forEach((log, index) => {
        console.group(`Log ${index + 1}: ${log.eventType} [${log.severity}]`);
        console.log('Timestamp:', log.timestamp);
        console.log('Message:', log.message);
        console.log('Screen:', log.screen ?? 'undefined (will be fixed in new logs)');
        console.log('User ID:', log.userId ?? 'undefined');
        console.log('Tenant ID:', log.tenantId ?? 'undefined');
        console.log('Session ID:', log.sessionId ?? 'undefined');
        console.log('Trace ID:', log.traceId ?? 'undefined');
        console.log('Platform:', log.platform ?? 'undefined');
        console.log('Device:', log.device ? 'present' : 'undefined');
        console.log('Locale:', log.locale ?? 'undefined');
        console.log('Timezone:', log.timezone ?? 'undefined');
        if (log.error) {
          console.log('Error:', log.error);
        }
        if (log.extra) {
          console.log('Extra:', log.extra);
        }
        // Show full log object for debugging
        console.log('Full log object:', log);
        console.groupEnd();
      });
    }
    console.groupEnd();
  }

  /**
   * Print all logs to console (use with caution - can be large)
   */
  printAllLogs() {
    this.printRecentLogs(this.logQueue.length);
  }

  /**
   * Test log - creates a test log entry
   * @param {string} severity - Log severity (INFO, WARN, ERROR, DEBUG)
   */
  async testLog(severity = 'INFO') {
    // Ensure screen is set if not already set
    if (!this.currentScreen && typeof window !== 'undefined' && window.location) {
      const route = window.location.pathname;
      const screenName = route.split('/').pop() || 'home';
      this.setCurrentScreen(screenName, route);
    }

    const testMessage = `Test ${severity.toLowerCase()} log - ${new Date().toISOString()}`;
    const testExtra = {
      test: true,
      timestamp: Date.now(),
      random: Math.random(),
    };

    switch (severity.toUpperCase()) {
      case 'ERROR':
        await this.logError('test_error', testMessage, null, testExtra);
        break;
      case 'WARN':
        await this.logWarning('test_warning', testMessage, testExtra);
        break;
      case 'DEBUG':
        await this.logDebug('test_debug', testMessage, testExtra);
        break;
      default:
        await this.logInfo('test_info', testMessage, testExtra);
    }

    console.log(`✅ Test ${severity} log created`);
  }

  /**
   * Manually trigger flush and log the result
   */
  async debugFlush() {
    console.log('🔄 Starting manual flush...');
    const beforeCount = this.logQueue.length;
    console.log(`📦 Logs before flush: ${beforeCount}`);

    if (beforeCount === 0) {
      console.log('⚠️ No logs to flush');
      return;
    }

    // Check if online
    if (!navigator.onLine) {
      console.warn('⚠️ Device is offline, cannot flush logs');
      return;
    }

    // Check if already flushing
    if (this.isFlushing) {
      console.warn('⚠️ Flush already in progress');
      return;
    }

    try {
      await this.flushLogs();
      const afterCount = this.logQueue.length;
      const flushedCount = beforeCount - afterCount;
      console.log(`📦 Logs after flush: ${afterCount}`);
      if (flushedCount > 0) {
        console.log(`✅ Flushed ${flushedCount} logs`);
        console.log('📡 Check Network tab for POST request to /mobile-logs');
      } else {
        console.warn('⚠️ No logs were flushed (check Network tab for errors)');
      }
    } catch (error) {
      console.error('❌ Flush error:', error);
      console.error('Error details:', error.message);
      throw error;
    }
  }
}

// Export singleton instance
const loggingService = new LoggingService();

// Expose to window for debugging in development
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.loggingService = loggingService;
  console.log(
    '🔧 Logging Service Debug Tools Available!\n' +
      'Try these commands in the console:\n' +
      '  - loggingService.printStatus() - Show service status\n' +
      '  - loggingService.printRecentLogs(10) - Show 10 recent logs\n' +
      '  - loggingService.getQueuedLogs() - Get all queued logs\n' +
      '  - loggingService.testLog("INFO") - Create a test log\n' +
      '  - loggingService.debugFlush() - Manually flush logs\n' +
      '  - loggingService.getStoredLogs() - Get logs from localStorage'
  );
}

export default loggingService;

